/// <reference types="vite/client" />
import { createInertiaApp, router } from '@inertiajs/react'
import { createRoot, hydrateRoot } from 'react-dom/client'
import { resolveMarketingPage } from './marketingApp'
import { analyticsNavigation, trackPageView } from '@/lib/analytics'
import type { SharedMarketingProps } from '@/types/marketing'
import { captureAttribution } from '@/lib/attribution'
import { recordPageView } from '@/lib/pageview'

// Capture UTM / click-ID attribution from the landing URL once per full page
// load (essential-tier, runs regardless of consent).
captureAttribution()

// First-party pageview for the initial load (anonymous, not consent-gated).
recordPageView('marketing')

router.on('invalid', (event) => {
  event.preventDefault()
  window.location.reload()
})

// SPA pageview tracking. Inertia fires `navigate` on the initial load and on
// every subsequent visit; the initial load is already counted by gtag/Pixel
// init, so we only fire when the path actually changes.
let lastTrackedPath =
  typeof window !== 'undefined' ? window.location.pathname + window.location.search : null

router.on('navigate', (event) => {
  // The sign-in pages ship no analytics config: never tracked, and a document
  // that already loaded a tag is swapped for a fresh one that has none.
  const config = (event.detail.page.props as Partial<SharedMarketingProps>).layout?.analytics
  const decision = analyticsNavigation(config)
  if (decision === 'reload') {
    window.location.reload()
    return
  }

  const current = window.location.pathname + window.location.search
  if (current === lastTrackedPath) return
  lastTrackedPath = current
  if (decision === 'track') trackPageView()
  recordPageView('marketing')
})

createInertiaApp({
  resolve: resolveMarketingPage,
  setup({ el, App, props }) {
    if (el.hasChildNodes()) {
      hydrateRoot(el, <App {...props} />)
      return
    }

    createRoot(el).render(<App {...props} />)
  },
  progress: { color: '#ff6a1a' },
})
